/**
 * M-036: EQS Admin UI
 *
 * Admin screen for managing the Extraction Quality System:
 * - Filter rules (L1 deterministic)
 * - Few-shot examples
 * - Entity aliases
 * - Golden test regression dashboard
 *
 * Backed by the EQS admin router (M-026 + F-26): /api/eqs/candidates and
 * the per-channel propose/test/approve/reject/disable endpoints.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/useAuth'
import { apiFetch, ApiError } from '../lib/api'
import { formatOfficerDate } from '../lib/officerFormat'
import {
  Filter,
  BookOpen,
  Link2,
  FlaskConical,
  Plus,
  Play,
  Check,
  X,
  Ban,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types — mirror backend EQSCandidateResponse + GoldenSuiteResult
// ---------------------------------------------------------------------------

type EqsTab = 'filter-rules' | 'few-shot' | 'aliases' | 'golden-tests'

type EQSChannel = 'few_shot' | 'filter' | 'alias'

type EQSStatus =
  | 'proposed'
  | 'testing'
  | 'pass'
  | 'fail'
  | 'pass_no_coverage'
  | 'approved'
  | 'rejected'
  | 'disabled'

type EQSTestResult = 'pass' | 'fail' | 'pass_no_coverage'

interface EQSCandidate {
  id: string
  channel: EQSChannel
  status: EQSStatus
  document_type: string | null
  extraction_call: string | null
  field: string | null
  source_text: string | null
  extracted_value: string | null
  corrected_value: string | null
  failure_signal: string | null
  failure_detail: string | null
  pattern: string | null
  pattern_field: string | null
  pattern_action: string | null
  canonical_name: string | null
  alias_name: string | null
  requires_table_flag: boolean | null
  requires_organigram_flag: boolean | null
  test_result: EQSTestResult | null
  test_detail: string | null
  reject_reason: string | null
  created_at: string
  updated_at: string
}

interface GoldenTestResult {
  test_id: string
  passed: boolean
  detail: string | null
}

interface GoldenSuiteResult {
  total: number
  passed: number
  failed: number
  results: GoldenTestResult[]
}

const TABS: { key: EqsTab; label: string; icon: React.ReactNode }[] = [
  { key: 'filter-rules', label: 'Filter Rules', icon: <Filter size={16} /> },
  { key: 'few-shot', label: 'Few-Shot Examples', icon: <BookOpen size={16} /> },
  { key: 'aliases', label: 'Entity Aliases', icon: <Link2 size={16} /> },
  { key: 'golden-tests', label: 'Golden Tests', icon: <FlaskConical size={16} /> },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string }> = {
    proposed: { bg: 'rgba(169,132,16,0.12)', text: 'var(--brand-gold)' },
    testing: { bg: 'rgba(12,79,171,0.12)', text: 'var(--brand-blue)' },
    pass: { bg: 'rgba(53,229,198,0.15)', text: 'var(--brand-verified)' },
    pass_no_coverage: { bg: 'rgba(53,229,198,0.10)', text: 'var(--brand-verified)' },
    fail: { bg: 'rgba(163,93,23,0.12)', text: 'var(--brand-rust)' },
    approved: { bg: 'rgba(53,229,198,0.15)', text: 'var(--brand-verified)' },
    rejected: { bg: 'rgba(163,93,23,0.12)', text: 'var(--brand-rust)' },
    disabled: { bg: 'rgba(148,163,184,0.15)', text: 'var(--brand-muted)' },
  }
  const s = styles[status] ?? { bg: 'var(--brand-line-soft)', text: 'var(--brand-muted)' }
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      {status}
    </span>
  )
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | undefined
    if (body && typeof body.detail === 'string') return body.detail
    return err.message
  }
  if (err instanceof Error) return err.message
  return 'Unexpected error'
}

const TERMINAL: ReadonlySet<EQSStatus> = new Set(['rejected', 'disabled'] as const)

// Lightweight loader / error UI shared across tabs.
function LoadingRow() {
  return (
    <div className="text-center py-8 text-slate-400 text-sm flex items-center justify-center gap-2">
      <RefreshCw size={14} className="animate-spin" /> Loading…
    </div>
  )
}

function ErrorRow({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="text-sm text-red-600 flex items-center gap-2 py-2">
      <AlertTriangle size={14} />
      <span>{message}</span>
      <button onClick={onRetry} className="btn-secondary text-xs ml-2">
        Retry
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Generic candidate loader
// ---------------------------------------------------------------------------

function useCandidates(channel: EQSChannel) {
  const [items, setItems] = useState<EQSCandidate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<EQSCandidate[]>(`/api/eqs/candidates?channel=${channel}`)
      setItems(data)
    } catch (err) {
      setError(describeError(err))
    } finally {
      setLoading(false)
    }
  }, [channel])

  useEffect(() => {
    reload()
  }, [reload])

  return { items, loading, error, reload }
}

async function rejectWithPrompt(
  url: string,
  defaultMessage = 'Rejection reason:'
): Promise<boolean> {
  const reason = window.prompt(defaultMessage)
  if (!reason || !reason.trim()) return false
  await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify({ reason: reason.trim() }),
  })
  return true
}

// ---------------------------------------------------------------------------
// Filter Rules Tab
// ---------------------------------------------------------------------------

function FilterRulesTab() {
  const { items, loading, error, reload } = useCandidates('filter')
  const [showCreate, setShowCreate] = useState(false)
  const [newPattern, setNewPattern] = useState('')
  const [newPatternField, setNewPatternField] = useState('entity_name')
  const [createError, setCreateError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionError(null)
    setBusy(true)
    try {
      await fn()
      await reload()
    } catch (err) {
      setActionError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const handleCreate = () =>
    runAction(async () => {
      setCreateError(null)
      if (!newPattern.trim()) {
        setCreateError('Pattern is required')
        throw new Error('Pattern is required')
      }
      await apiFetch('/api/eqs/filter-rules', {
        method: 'POST',
        body: JSON.stringify({
          channel: 'filter',
          pattern: newPattern.trim(),
          pattern_field: newPatternField.trim() || 'entity_name',
          pattern_action: 'remove',
        }),
      })
      setShowCreate(false)
      setNewPattern('')
      setNewPatternField('entity_name')
    })

  const handleTest = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/filter-rules/${id}/test`, { method: 'POST' }))

  const handleApprove = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/filter-rules/${id}/approve`, { method: 'POST' }))

  const handleReject = (id: string) =>
    runAction(async () => {
      const ok = await rejectWithPrompt(`/api/eqs/filter-rules/${id}/reject`)
      if (!ok) throw new Error('Rejection cancelled')
    })

  const handleDisable = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/filter-rules/${id}/disable`, { method: 'PATCH' }))

  if (loading) return <LoadingRow />
  if (error) return <ErrorRow message={error} onRetry={reload} />

  return (
    <div className="space-y-4">
      {actionError && <ErrorRow message={actionError} onRetry={reload} />}

      {items.length === 0 && (
        <div className="text-center py-8 text-slate-400 text-sm">No filter rules yet.</div>
      )}

      {items.map((rule) => (
        <div key={rule.id} className="border border-slate-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedId(expandedId === rule.id ? null : rule.id)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedId === rule.id ? (
                <ChevronDown size={14} className="text-slate-400" />
              ) : (
                <ChevronRight size={14} className="text-slate-400" />
              )}
              <span
                className="text-slate-800 font-medium"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {rule.pattern ?? '(no pattern)'}
              </span>
              <StatusBadge status={rule.status} />
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>L1 deterministic</span>
              <span>field: {rule.pattern_field ?? '—'}</span>
            </div>
          </button>

          {expandedId === rule.id && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 space-y-3">
              <div className="text-xs text-slate-500">
                Created {new Date(rule.created_at).toLocaleString()} · updated{' '}
                {new Date(rule.updated_at).toLocaleString()}
              </div>

              {rule.test_result && (
                <div className="flex items-center gap-2 text-xs">
                  {rule.test_result === 'fail' ? (
                    <XCircle size={12} className="text-red-500" />
                  ) : (
                    <CheckCircle size={12} className="text-green-500" />
                  )}
                  <span className="text-slate-700">
                    last test: {rule.test_result}
                    {rule.test_detail ? ` — ${rule.test_detail}` : ''}
                  </span>
                </div>
              )}

              {rule.reject_reason && (
                <div className="text-xs text-red-600">Rejection reason: {rule.reject_reason}</div>
              )}

              <div className="flex flex-wrap gap-2">
                {rule.status === 'proposed' && (
                  <button
                    onClick={() => handleTest(rule.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1"
                  >
                    <Play size={12} /> Test against golden set
                  </button>
                )}
                {(rule.status === 'pass' || rule.status === 'pass_no_coverage') && (
                  <button
                    onClick={() => handleApprove(rule.id)}
                    disabled={busy}
                    className="btn-primary text-xs flex items-center gap-1"
                  >
                    <Check size={12} /> Approve
                  </button>
                )}
                {rule.status === 'fail' && (
                  <span className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle size={12} /> Cannot approve — golden test fail
                  </span>
                )}
                {rule.status === 'proposed' && (
                  <span
                    className="text-xs text-slate-400 flex items-center gap-1"
                    title="Test against golden set before approving"
                  >
                    <Clock size={12} /> Test required before approval
                  </span>
                )}
                {!TERMINAL.has(rule.status) && rule.status !== 'approved' && (
                  <button
                    onClick={() => handleReject(rule.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1 text-red-600"
                  >
                    <X size={12} /> Reject
                  </button>
                )}
                {rule.status === 'approved' && (
                  <button
                    onClick={() => handleDisable(rule.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Ban size={12} /> Disable
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {showCreate ? (
        <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
          <h4 className="text-sm font-semibold text-slate-700">New filter rule</h4>
          <input
            placeholder="Pattern (regex or literal)"
            value={newPattern}
            onChange={(e) => {
              setNewPattern(e.target.value)
              setCreateError(null)
            }}
            className="input-field w-full"
          />
          <input
            placeholder="Pattern field (default: entity name)"
            value={newPatternField}
            onChange={(e) => setNewPatternField(e.target.value)}
            className="input-field w-full"
          />
          {createError && (
            <div className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle size={12} /> {createError}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={busy || !newPattern.trim()}
              className="btn-primary text-sm"
            >
              Create (proposed)
            </button>
            <button
              onClick={() => {
                setShowCreate(false)
                setCreateError(null)
              }}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowCreate(true)}
          className="btn-secondary text-sm flex items-center gap-1"
        >
          <Plus size={14} /> Create rule
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Few-Shot Examples Tab
// ---------------------------------------------------------------------------

function FewShotTab() {
  const { items, loading, error, reload } = useCandidates('few_shot')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionError(null)
    setBusy(true)
    try {
      await fn()
      await reload()
    } catch (err) {
      setActionError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const handleTest = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/few-shots/${id}/test`, { method: 'POST' }))

  const handleApprove = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/few-shots/${id}/approve`, { method: 'POST' }))

  const handleReject = (id: string) =>
    runAction(async () => {
      const ok = await rejectWithPrompt(`/api/eqs/few-shots/${id}/reject`)
      if (!ok) throw new Error('Rejection cancelled')
    })

  const handleDisable = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/few-shots/${id}/disable`, { method: 'PATCH' }))

  if (loading) return <LoadingRow />
  if (error) return <ErrorRow message={error} onRetry={reload} />

  return (
    <div className="space-y-3">
      {actionError && <ErrorRow message={actionError} onRetry={reload} />}

      {items.length === 0 && (
        <div className="text-center py-8 text-slate-400 text-sm">
          No few-shot examples yet. Candidates are produced by the corrections flow on the
          workbench.
        </div>
      )}

      {items.map((ex) => (
        <div key={ex.id} className="border border-slate-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedId(expandedId === ex.id ? null : ex.id)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedId === ex.id ? (
                <ChevronDown size={14} className="text-slate-400" />
              ) : (
                <ChevronRight size={14} className="text-slate-400" />
              )}
              <span
                className="text-slate-800 font-medium"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {ex.document_type ?? '—'} / {ex.extraction_call ?? '—'} / {ex.field ?? '—'}
              </span>
              <StatusBadge status={ex.status} />
            </div>
            <span className="text-xs text-slate-500">{formatOfficerDate(ex.created_at)}</span>
          </button>

          {expandedId === ex.id && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-slate-500 block mb-1">Source text</span>
                  <div
                    className="bg-white border border-slate-200 rounded p-2 text-slate-700"
                    style={{ fontFamily: 'var(--font-mono)' }}
                  >
                    {ex.source_text ?? '—'}
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-red-500 block mb-1">Extracted (wrong)</span>
                    <div
                      className="bg-red-50 border border-red-200 rounded p-2 text-slate-700"
                      style={{ fontFamily: 'var(--font-mono)' }}
                    >
                      {ex.extracted_value ?? '—'}
                    </div>
                  </div>
                  <div>
                    <span className="text-green-600 block mb-1">Corrected (right)</span>
                    <div
                      className="bg-green-50 border border-green-200 rounded p-2 text-slate-700"
                      style={{ fontFamily: 'var(--font-mono)' }}
                    >
                      {ex.corrected_value ?? '—'}
                    </div>
                  </div>
                </div>
              </div>

              {ex.failure_signal && (
                <div className="text-xs text-slate-500">
                  Failure signal: <span className="font-medium">{ex.failure_signal}</span>
                  {ex.failure_detail ? ` — ${ex.failure_detail}` : ''}
                </div>
              )}
              <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={!!ex.requires_table_flag} disabled readOnly />
                  Scoped to documents that contain a table
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={!!ex.requires_organigram_flag}
                    disabled
                    readOnly
                  />
                  Scoped to documents that contain an organigram
                </label>
              </div>
              {ex.reject_reason && (
                <div className="text-xs text-red-600">Rejection reason: {ex.reject_reason}</div>
              )}
              {ex.test_result && (
                <div className="flex items-center gap-2 text-xs">
                  {ex.test_result === 'fail' ? (
                    <XCircle size={12} className="text-red-500" />
                  ) : (
                    <CheckCircle size={12} className="text-green-500" />
                  )}
                  <span className="text-slate-700">
                    last test: {ex.test_result}
                    {ex.test_detail ? ` — ${ex.test_detail}` : ''}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {ex.status === 'proposed' && (
                  <button
                    onClick={() => handleTest(ex.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1"
                  >
                    <Play size={12} /> Test
                  </button>
                )}
                {(ex.status === 'pass' || ex.status === 'pass_no_coverage') && (
                  <button
                    onClick={() => handleApprove(ex.id)}
                    disabled={busy}
                    className="btn-primary text-xs flex items-center gap-1"
                  >
                    <Check size={12} /> Approve
                  </button>
                )}
                {ex.status === 'proposed' && (
                  <span
                    className="text-xs text-slate-400 flex items-center gap-1"
                    title="Test against golden set before approving"
                  >
                    <Clock size={12} /> Test required before approval
                  </span>
                )}
                {ex.status === 'fail' && (
                  <span className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle size={12} /> Cannot approve — golden test fail
                  </span>
                )}
                {!TERMINAL.has(ex.status) && ex.status !== 'approved' && (
                  <button
                    onClick={() => handleReject(ex.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1 text-red-600"
                  >
                    <X size={12} /> Reject
                  </button>
                )}
                {ex.status === 'approved' && (
                  <button
                    onClick={() => handleDisable(ex.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Ban size={12} /> Disable
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Entity Aliases Tab
// ---------------------------------------------------------------------------

function AliasesTab() {
  const { items, loading, error, reload } = useCandidates('alias')
  const [showAdd, setShowAdd] = useState(false)
  const [newCanonical, setNewCanonical] = useState('')
  const [newAlias, setNewAlias] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionError(null)
    setBusy(true)
    try {
      await fn()
      await reload()
    } catch (err) {
      setActionError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const handleAdd = () =>
    runAction(async () => {
      setCreateError(null)
      if (!newCanonical.trim() || !newAlias.trim()) {
        setCreateError('Canonical name and alias are required')
        throw new Error('Canonical name and alias are required')
      }
      await apiFetch('/api/eqs/aliases', {
        method: 'POST',
        body: JSON.stringify({
          channel: 'alias',
          canonical_name: newCanonical.trim(),
          alias_name: newAlias.trim(),
        }),
      })
      setShowAdd(false)
      setNewCanonical('')
      setNewAlias('')
    })

  const handleApprove = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/aliases/${id}/approve`, { method: 'POST' }))

  const handleReject = (id: string) =>
    runAction(async () => {
      const ok = await rejectWithPrompt(`/api/eqs/aliases/${id}/reject`)
      if (!ok) throw new Error('Rejection cancelled')
    })

  const handleDisable = (id: string) =>
    runAction(() => apiFetch(`/api/eqs/aliases/${id}/disable`, { method: 'PATCH' }))

  if (loading) return <LoadingRow />
  if (error) return <ErrorRow message={error} onRetry={reload} />

  return (
    <div className="space-y-4">
      {actionError && <ErrorRow message={actionError} onRetry={reload} />}

      {items.length === 0 && (
        <div className="text-center py-8 text-slate-400 text-sm">No entity aliases yet.</div>
      )}

      {items.map((a) => (
        <div key={a.id} className="border border-slate-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedId === a.id ? (
                <ChevronDown size={14} className="text-slate-400" />
              ) : (
                <ChevronRight size={14} className="text-slate-400" />
              )}
              <span
                className="text-slate-800 font-medium"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {a.canonical_name ?? '—'} <span className="text-slate-400">→</span>{' '}
                {a.alias_name ?? '—'}
              </span>
              <StatusBadge status={a.status} />
            </div>
            <span className="text-xs text-slate-500">{formatOfficerDate(a.created_at)}</span>
          </button>

          {expandedId === a.id && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 space-y-3">
              {a.reject_reason && (
                <div className="text-xs text-red-600">Rejection reason: {a.reject_reason}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {a.status === 'proposed' && (
                  <button
                    onClick={() => handleApprove(a.id)}
                    disabled={busy}
                    className="btn-primary text-xs flex items-center gap-1"
                  >
                    <Check size={12} /> Approve
                  </button>
                )}
                {!TERMINAL.has(a.status) && a.status !== 'approved' && (
                  <button
                    onClick={() => handleReject(a.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1 text-red-600"
                  >
                    <X size={12} /> Reject
                  </button>
                )}
                {a.status === 'approved' && (
                  <button
                    onClick={() => handleDisable(a.id)}
                    disabled={busy}
                    className="btn-secondary text-xs flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Ban size={12} /> Disable
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {showAdd ? (
        <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
          <h4 className="text-sm font-semibold text-slate-700">New alias</h4>
          <div className="grid grid-cols-2 gap-3">
            <input
              placeholder="Canonical entity name"
              value={newCanonical}
              onChange={(e) => {
                setNewCanonical(e.target.value)
                setCreateError(null)
              }}
              className="input-field"
            />
            <input
              placeholder="Alias"
              value={newAlias}
              onChange={(e) => {
                setNewAlias(e.target.value)
                setCreateError(null)
              }}
              className="input-field"
            />
          </div>
          {createError && (
            <div className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle size={12} /> {createError}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={busy || !newCanonical.trim() || !newAlias.trim()}
              className="btn-primary text-sm"
            >
              Add (proposed)
            </button>
            <button
              onClick={() => {
                setShowAdd(false)
                setCreateError(null)
              }}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAdd(true)}
          className="btn-secondary text-sm flex items-center gap-1"
        >
          <Plus size={14} /> Add alias
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Golden Tests Tab
// ---------------------------------------------------------------------------

function GoldenTestsTab() {
  const [suites, setSuites] = useState<GoldenSuiteResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<GoldenSuiteResult[]>('/api/eqs/golden-tests')
      setSuites(data)
    } catch (err) {
      setError(describeError(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  if (loading) return <LoadingRow />
  if (error) return <ErrorRow message={error} onRetry={reload} />

  const latest = suites[0]
  const totalsEmpty = !latest || (latest.total === 0 && latest.results.length === 0)

  return (
    <div className="space-y-4">
      {totalsEmpty ? (
        <div className="text-center py-8 text-slate-400 text-sm">
          No golden-suite runs yet. The suite runner is not yet wired (see F-26-followup); this view
          will populate once the runner ships.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle size={16} /> {latest.passed} passing
            </span>
            {latest.failed > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <XCircle size={16} /> {latest.failed} failing
              </span>
            )}
            <span className="text-slate-400 text-xs">{latest.total} total</span>
          </div>

          {latest.results.map((r) => (
            <div
              key={r.test_id}
              className="border border-slate-200 rounded-lg px-4 py-3 text-sm flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                {r.passed ? (
                  <CheckCircle size={16} className="text-green-500" />
                ) : (
                  <XCircle size={16} className="text-red-500" />
                )}
                <span
                  className="text-slate-800 font-medium"
                  style={{ fontFamily: 'var(--font-mono)' }}
                >
                  {r.test_id}
                </span>
              </div>
              {r.detail && <span className="text-xs text-slate-500">{r.detail}</span>}
            </div>
          ))}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function EqsAdminPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<EqsTab>('filter-rules')

  if (!user || user.role !== 'admin') {
    return (
      <div
        className="bg-white p-6"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        <p className="text-sm text-red-600">Access denied</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div
        className="bg-white p-6"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        <h2 className="text-lg font-semibold text-slate-900">EQS Admin</h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage filter rules, few-shot examples, entity aliases, and golden test regressions.
        </p>
      </div>

      <div
        className="bg-white"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-nolos-primary text-nolos-primary font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'filter-rules' && <FilterRulesTab />}
          {activeTab === 'few-shot' && <FewShotTab />}
          {activeTab === 'aliases' && <AliasesTab />}
          {activeTab === 'golden-tests' && <GoldenTestsTab />}
        </div>
      </div>
    </div>
  )
}
